// Per-dialect-fork normalizer factory. Slice 3.5 extraction (n=3 forks). The
// Slice 2.5 PROGRESS log predicted ≥ 95% fork-vs-fork structural overlap;
// measurement on n=3 confirmed it. Before this factory, each per-dialect
// `index.ts` was ~100 lines of near-identical code differing only in:
//   1. the expected engine name (`oracle` / `mssql` / `postgres`),
//   2. the parser-singleton import (`getOracleParser` / `getMssqlParser` / …),
//   3. the error-message prefix strings (`normalize/oracle:` / …).
//
// All three are closed over by this factory. Each per-dialect `index.ts`
// becomes a 5-line file that imports its parser loader and re-exports the
// factory's output.

import type { Parser } from 'web-tree-sitter';

import type {
  DocumentStructure,
  ParseOptions,
  ParsedDocument,
  RecoveryInfo,
  SqlEngine,
  SqlRange,
} from '../../model';
import { SCHEMA_VERSION } from '../../model';
import {
  buildLineIndex,
  classifyStatement,
  extractSchemaEffects,
  getDocumentStructure as scannerGetDocumentStructure,
  scanDocument,
  type ScanStatement,
} from '../../document';
import { statementId } from '../../document/ids';
import { rangeFromOffsets, type LineIndex } from '../../document/lineIndex';

import { buildRecoveryInfoFromTree } from './tree';

export interface ForkNormalizerOptions {
  /** Engine name guarded on `ParseOptions.engine`; errors if mismatched. */
  readonly engine: Extract<SqlEngine, 'oracle' | 'mssql' | 'postgres' | 'mysql' | 'sqlite'>;
  /** Lazy per-fork parser singleton, keyed to the fork's wasm loader. */
  readonly getParser: () => Promise<Parser>;
}

export interface ForkNormalizer {
  buildParsedDocument(source: string, options: ParseOptions): Promise<ParsedDocument>;
  getDocumentStructure(doc: ParsedDocument, documentId: string): DocumentStructure;
}

function shiftRange(range: SqlRange, offset: number, fullIdx: LineIndex): SqlRange {
  const start = range.startOffset + offset;
  const end = range.endOffset + offset;
  return rangeFromOffsets(fullIdx, start, end);
}

async function recoverPerStatement(
  engine: ForkNormalizerOptions['engine'],
  s: ScanStatement,
  fullIdx: LineIndex,
  parser: Parser,
): Promise<RecoveryInfo> {
  const tree = parser.parse(s.text);
  if (!tree) {
    throw new Error(`normalize/${engine}: tree-sitter parse returned null`);
  }
  try {
    const localIdx = buildLineIndex(s.text);
    const localRecovery = buildRecoveryInfoFromTree(tree.rootNode, localIdx);
    return {
      errorRanges: localRecovery.errorRanges.map((r) => shiftRange(r, s.startOffset, fullIdx)),
      missingRanges: localRecovery.missingRanges.map((r) => shiftRange(r, s.startOffset, fullIdx)),
      truncated: localRecovery.truncated,
      confidence: localRecovery.confidence,
    };
  } finally {
    tree.delete();
  }
}

export function createForkNormalizer(opts: ForkNormalizerOptions): ForkNormalizer {
  const { engine, getParser } = opts;

  async function buildParsedDocument(
    source: string,
    options: ParseOptions,
  ): Promise<ParsedDocument> {
    if (options.engine !== engine) {
      throw new Error(
        `normalize/${engine}: expected engine="${engine}", got "${options.engine}"`,
      );
    }
    const parser = await getParser();
    const idx = buildLineIndex(source);
    const scan = scanDocument(source, options);
    const statements = scan.statements.map((s) => {
      const { kind, risk } = classifyStatement(s.text, engine);
      return {
        id: statementId(s.text, s.startOffset),
        range: rangeFromOffsets(idx, s.startOffset, s.endOffset),
        kind,
        risk,
        readsFrom: [],
        writesTo: [],
        schemaEffects: extractSchemaEffects(s.text, engine),
      };
    });

    const errorRanges: SqlRange[] = [];
    const missingRanges: SqlRange[] = [];
    let truncated = false;
    let confidence: RecoveryInfo['confidence'] = 'high';
    for (const s of scan.statements) {
      const rec = await recoverPerStatement(engine, s, idx, parser);
      errorRanges.push(...rec.errorRanges);
      missingRanges.push(...rec.missingRanges);
      if (rec.truncated) truncated = true;
      if (rec.confidence === 'low') confidence = 'low';
    }
    const recovery: RecoveryInfo = { errorRanges, missingRanges, truncated, confidence };

    return { source, options, statements, recovery, schemaVersion: SCHEMA_VERSION };
  }

  function getDocumentStructure(doc: ParsedDocument, documentId: string): DocumentStructure {
    return scannerGetDocumentStructure(doc, documentId);
  }

  return { buildParsedDocument, getDocumentStructure };
}
